/**
 * React-хуки kikayoo: клиентский обратный отсчёт по серверным `startedAt`/
 * `durationMs` (только UX; истина — на сервере, протокол §5) и обвязка над
 * [`QuizSocket`](./ws.ts) с поддержкой реконнекта и стабильными колбэками.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientMessage, Role, ServerMessage, StartPayload } from './protocol';
import { secondsLeft } from './protocol';
import { QuizSocket, type HostCommand, type SocketStatus } from './ws';

/**
 * Обратный отсчёт вопроса. Пересчитывается каждые 250 мс из серверных меток;
 * не управляет завершением — по истечении сервер сам пришлёт reveal/stats.
 * `null`-аргументы → нет активного вопроса, возвращает 0.
 *
 * `paused` (Фаза 10, И2 — Баг-Т): заморозка отсчёта — интервал гасится,
 * возвращается последнее вычисленное значение (при «холодном» старте сразу с
 * `paused` — текущее `secondsLeft`, не 0). Снятие `paused` возобновляет тик от
 * тех же серверных меток `startedAt + durationMs`. Без параметра — прежнее
 * поведение (обратная совместимость).
 */
export function useCountdown(
  startedAt: number | null,
  durationMs: number | null,
  paused = false,
): number {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (startedAt === null || durationMs === null) {
      setLeft(0);
      return;
    }
    if (paused) {
      // Замороженный отсчёт: фиксируем последнее значение; для повторного
      // монтирования сразу в паузе (reconnect с уже данным ответом) берём
      // текущий остаток, а не 0.
      setLeft((prev) => (prev > 0 ? prev : secondsLeft(startedAt, durationMs)));
      return;
    }
    const tick = (): void => setLeft(secondsLeft(startedAt, durationMs));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [startedAt, durationMs, paused]);
  return left;
}

export interface UseQuizSocketOptions {
  /**
   * Ник для авто-join при открытии соединения (и при реконнекте). Для ведущего
   * не передаётся; для игрока — его ник. Join отправляется хуком сам, поэтому
   * потребителю не нужно дергать sendJoin вручную в onOpen.
   */
  joinNick?: string;
  /**
   * Геттер device-токена игрока (Фаза 13, Эпик A2): вызывается при КАЖДОМ
   * открытии сокета (первичный join + реконнекты), чтобы токен читался из
   * localStorage на момент отправки, а не на момент создания хука. Для
   * ведущего не передаётся.
   */
  joinToken?: () => string | undefined;
  /** Вызывается для каждого разобранного серверного сообщения. */
  onMessage?: (message: ServerMessage) => void;
  /** Вызывается при смене статуса соединения. */
  onStatus?: (status: SocketStatus) => void;
  /** Фабрика WebSocket (для тестов). */
  factory?: (url: string) => WebSocket;
}

export interface UseQuizSocket {
  status: SocketStatus;
  /** Низкоуровневая отправка конверта. */
  send: (message: ClientMessage) => void;
  /** Отправить answer (идемпотентно на сервере). */
  sendAnswer: (questionId: number, option: number) => void;
  /** Отправить host-команду start/next/close (no-op для игрока). Payload —
   *  опции для `start` (scorePause, И4); next/close игнорируют его. */
  sendHostCommand: (command: HostCommand, payload?: StartPayload) => void;
  /** Отправить rename своего ника (no-op для хоста; фаза lobby). */
  sendRename: (nick: string) => void;
  /** Отправить rename чужого ника ведущим (no-op для игрока). */
  sendRenamePlayer: (playerId: number, nick: string) => void;
  /** Отправить host-команду kick — удалить участника (no-op для игрока; Фаза 15, B2). */
  sendKick: (playerId: number) => void;
}

/**
 * Подключает QuizSocket к комнате `pin` в роли `role` и сам шлёт `join` при
 * каждом открытии (включая реконнект). Колбэки берутся через ref — socket
 * создаётся один раз на pin/role, свежие обработчики вызываются без
 * пересоздания соединения. Размонтирование → close().
 */
export function useQuizSocket(
  pin: string,
  role: Role,
  options: UseQuizSocketOptions,
): UseQuizSocket {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [status, setStatus] = useState<SocketStatus>('idle');
  const socketRef = useRef<QuizSocket | null>(null);

  useEffect(() => {
    const socket = new QuizSocket({
      pin,
      role,
      factory: optionsRef.current.factory,
      onOpen: () => socket.sendJoin(optionsRef.current.joinNick, optionsRef.current.joinToken?.()),
      onGiveUp: () => optionsRef.current.onStatus?.('closed'),
    });
    socket.onStatus((s) => {
      setStatus(s);
      optionsRef.current.onStatus?.(s);
    });
    socket.onMessage((m) => optionsRef.current.onMessage?.(m));
    socketRef.current = socket;
    socket.connect();
    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [pin, role]);

  const send = useCallback((message: ClientMessage) => {
    socketRef.current?.send(message);
  }, []);

  const sendAnswer = useCallback((questionId: number, option: number) => {
    socketRef.current?.sendAnswer(questionId, option);
  }, []);

  const sendHostCommand = useCallback((command: HostCommand, payload?: StartPayload) => {
    socketRef.current?.sendHostCommand(command, payload);
  }, []);

  const sendRename = useCallback((nick: string) => {
    socketRef.current?.sendRename(nick);
  }, []);

  const sendRenamePlayer = useCallback((playerId: number, nick: string) => {
    socketRef.current?.sendRenamePlayer(playerId, nick);
  }, []);

  const sendKick = useCallback((playerId: number) => {
    socketRef.current?.sendKick(playerId);
  }, []);

  return { status, send, sendAnswer, sendHostCommand, sendRename, sendRenamePlayer, sendKick };
}
